import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { UtilisateurSearchList, useModal } from '@europrocurement/flexy-components';
import { Contact } from '@europrocurement/l2d-domain';
import { UtilisateursJsonldUserworkgroupRead } from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

export type ContactSelectionModalProps = {
    contacts: Contact[];
    currentContactId?: string;
    onContactSelect: (contact: Contact) => void;
    isLoading?: boolean;
};

/**
 * Modal de sélection d'un contact parmi une liste de contacts du prescripteur.
 * Permet de modifier l'assignation d'un dossier à un contact spécifique.
 */
const ContactSelectionModal: React.FunctionComponent<ContactSelectionModalProps> = function ({
    contacts,
    currentContactId,
    onContactSelect,
    isLoading = false,
}) {
    const { modalActions } = useModal();

    const [selectedContact, setSelectedContact] =
        useState<UtilisateursJsonldUserworkgroupRead | null>(null);

    useEffect(() => {
        if (currentContactId) {
            const current = contacts.find((c) => c.id?.toString() === currentContactId.toString());
            if (current) {
                setSelectedContact({
                    id: current.id,
                    nom: current.nomContact || '',
                    prenom: current.prenomContact || '',
                    mail: current.emailsContact[0]?.email || '',
                } as UtilisateursJsonldUserworkgroupRead);
            }
        }
    }, [currentContactId, contacts]);

    const handleContactSelect = (user: UtilisateursJsonldUserworkgroupRead) => {
        const contact = contacts.find((c) => c.id?.toString() === user.id?.toString());
        if (contact) {
            onContactSelect(contact);
            modalActions.close();
        }
    };

    const contactsForList: UtilisateursJsonldUserworkgroupRead[] = contacts.map(
        (contact) =>
            ({
                id: contact.id,
                nom: contact.nomContact || '',
                prenom: contact.prenomContact || '',
                mail: contact.emailsContact[0]?.email || '',
            }) as UtilisateursJsonldUserworkgroupRead,
    );

    return (
        <Box
            sx={{
                width: '600px',
                padding: '30px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
            }}
        >
            <Box>
                <Typography variant="l2dh6">Modifier le contact assigné</Typography>
                <Box sx={{ marginTop: '16px', marginBottom: '16px' }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Sélectionnez un contact du prescripteur pour l&apos;assigner à ce dossier :
                    </Typography>
                </Box>
            </Box>

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                </Box>
            )}

            {!isLoading && contacts.length > 0 && (
                <UtilisateurSearchList
                    users={contactsForList}
                    selectedUser={selectedContact}
                    setSelectedUser={(user) => {
                        if (user && typeof user !== 'function') {
                            handleContactSelect(user);
                        }
                    }}
                    listTitle="Contacts disponibles"
                    userInfo={(user) => user.mail || ''}
                />
            )}

            {!isLoading && contacts.length === 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: '32px',
                        color: 'text.secondary',
                    }}
                >
                    <Typography variant="body2">
                        Aucun contact disponible pour ce prescripteur
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default ContactSelectionModal;
